import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Trans, useTranslations } from "adapters/i18n";
import React, { useCallback, useMemo, useState } from "react";
import { DATE_UNIT, DateUtils } from "shared/dateUtils";
import { getIsInternalUserFromStorage } from "shared/storageUtils";
import { type NotificationItem, NotificationType } from "../notification.types";
import { getSuspendedClients } from "../utils/notificationUtils";

type UseGetSuspendedClientNotificationsProps = {
  clients: unknown[];
  setViewedNotificationsInCookies: (notificationId: string) => void;
};

export function useGetSuspendedClientNotifications({
  clients,
  setViewedNotificationsInCookies,
}: UseGetSuspendedClientNotificationsProps) {
  const { t } = useTranslations();

  const [viewedNotifications, setViewedNotifications] = useState<string[]>([]);

  const isInternalUser = useMemo(() => getIsInternalUserFromStorage(), []);

  const onActionShowNotification = useCallback(
    (notificationId: string) => {
      setViewedNotifications((prev) => {
        if (prev.includes(notificationId)) return prev;
        setViewedNotificationsInCookies(notificationId);
        return [...prev, notificationId];
      });
    },
    [setViewedNotificationsInCookies]
  );

  const getClientNotifications = useCallback((): NotificationItem[] => {
    if (!isInternalUser) {
      return [];
    }

    const baseDate = DateUtils.now().subtract(7, DATE_UNIT.DAY);
    const suspendedClients = getSuspendedClients(clients);
    const targetClients = suspendedClients.filter((item) =>
      DateUtils.parse(item.eventDate).isAfter(baseDate)
    );

    return targetClients.map((item) => {
      const notificationId = `cl${item.id}${DateUtils.parse(
        item.eventDate
      ).valueOf()}`;

      return {
        actionOnShow: () => {
          onActionShowNotification(notificationId);
        },
        description: (
          <Trans
            i18nKey="errors:client.access-error"
            values={{ name: item.name }}
            components={{ bold: <strong /> }}
          />
        ),
        icon: <WarningAmberOutlined style={{ color: "#FFC944" }} />,
        id: notificationId,
        title: t("common:status.attention"),
        type: NotificationType.ClientAccess,
      };
    });
  }, [clients, isInternalUser, onActionShowNotification, t]);

  return {
    viewedNotifications,
    getClientNotifications,
    setViewedNotifications,
  };
}
